/**
 * Tracker's visual language — now the whole app's.
 *
 * The palette moved to `constants/ui.js` so every page can share it; this file
 * re-exports under the original names so Tracker's components keep working
 * untouched, and stays the home for the Tracker-specific vocabulary below.
 */
import { COLORS, MONO as SHARED_MONO } from "./ui";

export const TRACKER_COLORS = COLORS;

export const MONO = SHARED_MONO;

/** How each stage status reads on a card. */
export const STATUS_BADGES = {
  pending_accept: { label: "Awaiting accept", bg: TRACKER_COLORS.sunken, color: TRACKER_COLORS.inkFaint, pulse: false },
  accepted: { label: "In progress", bg: TRACKER_COLORS.accentSoft, color: TRACKER_COLORS.accentInk, pulse: true },
  blocked: { label: "Blocked", bg: TRACKER_COLORS.warnSoft, color: TRACKER_COLORS.warn, pulse: false },
  rejected: { label: "Sent back", bg: TRACKER_COLORS.dangerSoft, color: TRACKER_COLORS.danger, pulse: false },
  done: { label: "Done", bg: TRACKER_COLORS.doneSoft, color: TRACKER_COLORS.done, pulse: false },
  not_started: { label: "Not started", bg: TRACKER_COLORS.sunken, color: TRACKER_COLORS.inkFaint, pulse: false },
  // Deliberately bypassed. Distinct from "not started", which only means the
  // work has not been reached yet.
  skipped: { label: "Skipped", bg: TRACKER_COLORS.sunken, color: TRACKER_COLORS.inkFaint, pulse: false },
};

/** The wall's lifecycle tabs. */
export const STATE_TABS = [
  { key: "active", label: "Active" },
  { key: "backlog", label: "Backlog" },
  { key: "on_hold", label: "On Hold", tone: "warn" },
  { key: "completed", label: "Completed" },
];

export const PRIORITY_STYLES = {
  Critical: { bg: TRACKER_COLORS.dangerSoft, color: TRACKER_COLORS.danger },
  High: { bg: TRACKER_COLORS.warnSoft, color: TRACKER_COLORS.warn },
  Medium: { bg: TRACKER_COLORS.sunken, color: TRACKER_COLORS.inkSoft },
  Low: { bg: TRACKER_COLORS.sunken, color: TRACKER_COLORS.inkFaint },
};

export function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export function formatShortDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/** "1 working day" / "4 working days" — one source of wording for durations. */
export function formatDays(n, { short = false } = {}) {
  if (n == null || Number.isNaN(Number(n))) return null;
  const v = Math.abs(Number(n));
  if (short) return `${v}d`;
  return `${v} working day${v === 1 ? "" : "s"}`;
}

/**
 * How actual days compare with the department's standard allowance.
 *
 * Graded on a tolerance band rather than exact equality — a stage a day over a
 * ten-day allowance isn't meaningfully late. Mirrors the approach the dashboard
 * already takes for budget pace and weekly progress.
 */
export const STAGE_TOLERANCE_DAYS = 1;

export function classifyVariance(actual, allowed) {
  if (actual == null || allowed == null || !allowed) return null;
  const diff = actual - allowed;
  if (diff > STAGE_TOLERANCE_DAYS) return "over";
  if (diff < -STAGE_TOLERANCE_DAYS) return "ahead";
  return "on_time";
}

export const VARIANCE_STYLES = {
  ahead: { label: "Ahead", bg: TRACKER_COLORS.doneSoft, color: TRACKER_COLORS.done },
  on_time: { label: "On time", bg: TRACKER_COLORS.accentSoft, color: TRACKER_COLORS.accentInk },
  over: { label: "Over", bg: TRACKER_COLORS.warnSoft, color: TRACKER_COLORS.warn },
};
